import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { Connector } from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

export function useDeleteConnector(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.deleteConnector(tenantId),
    mutationFn: async (connectorId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const success = await connectorService.deleteConnector(
        tenantId,
        connectorId
      );
      if (!success) {
        throw new Error('Failed to delete connector');
      }

      await pollForResourceGone(() =>
        connectorService.findConnector(tenantId, connectorId)
      );
      return connectorId;
    },
    onMutate: async (connectorId: UUID) => {
      if (!tenantId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: connectorQueryKeys.listByTenant(tenantId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Connector>>>({
        queryKey: connectorQueryKeys.listByTenant(tenantId),
      });

      qc.setQueriesData<InfiniteData<Page<Connector>>>(
        { queryKey: connectorQueryKeys.listByTenant(tenantId) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page, index) => ({
              ...page,
              items: page.items.filter((c) => c.id !== connectorId),
              total: index === 0 ? (page.total ?? 0) - 1 : page.total,
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _connectorId, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: connectorQueryKeys.listByTenant(tenantId),
      });
    },
  });
}
